import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV, BRAND } from "@/lib/brand";
import { BrandLogoLink } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-forest text-ivory/80" data-testid="announcement-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-center lg:justify-between text-[10.5px] uppercase tracking-[0.26em]">
          <span className="hidden lg:inline">{BRAND.descriptor}</span>
          <span>{BRAND.participation}</span>
          <span className="hidden lg:inline text-champagne">{BRAND.tagline}</span>
        </div>
      </div>

      <div className={`glass border-b transition-colors duration-500 ${scrolled ? "border-forest/15" : "border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-[height] duration-500 ${scrolled ? "h-16 sm:h-[72px]" : "h-20 sm:h-24"}`}>
            <BrandLogoLink />

            <nav className="hidden lg:flex items-center gap-10">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={isActive(item.to) ? "page" : undefined}
                  data-testid={`navbar-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`link-underline text-[11.5px] uppercase tracking-[0.22em] font-medium transition-colors ${
                    isActive(item.to) ? "text-forest" : "text-charcoal/65 hover:text-forest"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-7">
              {user ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid="navbar-link-admin" className="link-underline text-[11.5px] uppercase tracking-[0.22em] font-medium text-charcoal/65 hover:text-forest transition-colors">
                      Admin
                    </Link>
                  )}
                  <Link to="/my-rituals" data-testid="navbar-link-my-rituals" className="btn btn-outline !px-6 !py-3">
                    {BRAND.dashboard}
                  </Link>
                </>
              ) : (
                <Link to="/join" data-testid="navbar-join-button" className="btn btn-primary !px-6 !py-3">
                  {BRAND.joinCta}
                </Link>
              )}
            </div>

            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              aria-expanded={open}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 text-forest"
            >
              {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-forest/10 bg-ivory" data-testid="mobile-menu">
            <div className="px-6 py-6 flex flex-col">
              {NAV.map((item, i) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="py-4 font-serif text-2xl text-forest border-b border-forest/10 animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-6 flex flex-col gap-3">
                {user ? (
                  <>
                    <Link to="/my-rituals" onClick={() => setOpen(false)} className="btn btn-outline">
                      {BRAND.dashboard}
                    </Link>
                    {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="btn btn-outline">Admin</Link>}
                  </>
                ) : (
                  <Link to="/join" onClick={() => setOpen(false)} className="btn btn-primary">
                    {BRAND.joinCta}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
